import { NavigateFunction, useNavigate, useParams } from "react-router";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { EditorApi } from "../../lib/api/editor-api";
import { Button } from "@/lib/components/Button/Button";
import AppHeader from "@/lib/components/AppHeader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/lib/components/Select";
import { useSidebar } from "@/lib/components/Sidebar";
import VideoListItem from "./VideoListItem";
import {
  IconExclamationCircle,
  IconFolder,
  IconFolderPlus,
  IconGift,
  IconVideo,
  IconVideoOff,
} from "@tabler/icons-react";
import { usePagedVideosQuery } from "@/lib/queries/video-queries";
import {
  useCreateFolderMutation,
  useFolderContentsQuery,
  useMoveFolderMutation,
  useMoveVideoToFolderMutation,
} from "@/lib/queries/folder-queries";
import { useInView } from "react-intersection-observer";
import { useEffect, useRef, useState } from "react";
import { paths } from "@/App";
import ReferralOverviewDialog from "@/features/Referral/ReferralOverviewDialog";
import VideosEmptyState from "./VideosEmptyState";
import EmptyState from "@/lib/components/EmptyState";
import LoadingSpinner from "@/lib/components/LoadingSpinner";
import NotificationsDropdown from "@/lib/components/NotificationsDropdown";
import VideoSearchBar from "./VideoSearchBar";
import FoldersSection from "./FoldersSection";
import Breadcrumb from "./Breadcrumb";
import NameDialog from "./NameDialog";
import { PagedVideoItemDto, VideoSort } from "@repo/api/video";
import { MAX_FOLDER_DEPTH } from "@repo/api/folder";
import Toast from "@/lib/components/Toast";
import { FolderDropData, snapAboveCursor, VideosDragData } from "./videos-dnd";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/lib/components/Tooltip";

const MAX_FOLDER_DEPTH_MESSAGE = `You can only nest folders up to ${MAX_FOLDER_DEPTH} levels deep.`;

async function handleCreateVideo(
  navigate: NavigateFunction,
  teamExternalId: string,
  folderExternalId?: string,
) {
  const video = await EditorApi.createVideo(teamExternalId, {
    folderExternalId: folderExternalId ?? null,
    source: "VIDEOS_PAGE",
  });

  navigate(paths.videoEdit(teamExternalId, video.externalId));
}

export default function Videos() {
  const { teamExternalId, folderExternalId } = useParams();

  const [createFolderOpen, setCreateFolderOpen] = useState(false);
  const [sort, setSort] = useState<VideoSort>("lastUpdated");

  const { data: contents } = useFolderContentsQuery(
    teamExternalId!,
    folderExternalId,
    sort,
  );
  const atMaxFolderDepth =
    (contents?.breadcrumb.length ?? 0) >= MAX_FOLDER_DEPTH;

  const handleCreateFolder = () => {
    if (atMaxFolderDepth) {
      Toast.info(MAX_FOLDER_DEPTH_MESSAGE);
      return;
    }
    setCreateFolderOpen(true);
  };

  const createFolderMutation = useCreateFolderMutation({
    teamExternalId: teamExternalId!,
    onSuccess: () => setCreateFolderOpen(false),
  });

  return (
    <>
      <VideosContent
        onCreateFolder={handleCreateFolder}
        sort={sort}
        onSortChange={setSort}
      />
      <NameDialog
        open={createFolderOpen}
        onOpenChange={setCreateFolderOpen}
        title="New folder"
        description="Organize your videos by grouping them into folders."
        placeholder="Folder name"
        submitLabel="Create"
        isSubmitting={createFolderMutation.isPending}
        onSubmit={(name) =>
          createFolderMutation.mutate({
            name,
            parentFolderExternalId: folderExternalId ?? null,
          })
        }
      />
    </>
  );
}

function VideosContent({
  onCreateFolder,
  sort,
  onSortChange,
}: {
  onCreateFolder: () => void;
  sort: VideoSort;
  onSortChange: (sort: VideoSort) => void;
}) {
  const navigate = useNavigate();
  const { isMobile } = useSidebar();
  const { teamExternalId, folderExternalId } = useParams();

  const [activeDrag, setActiveDrag] = useState<VideosDragData | null>(null);
  const dragJustEndedRef = useRef(false);
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
  );

  const moveFolderMutation = useMoveFolderMutation({
    teamExternalId: teamExternalId!,
  });
  const moveVideoMutation = useMoveVideoToFolderMutation({
    teamExternalId: teamExternalId!,
  });

  const handleDragStart = (event: DragStartEvent) => {
    setActiveDrag((event.active.data.current as VideosDragData) ?? null);
  };

  const markDragEnded = () => {
    setActiveDrag(null);
    dragJustEndedRef.current = true;
    setTimeout(() => {
      dragJustEndedRef.current = false;
    }, 0);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    markDragEnded();

    const dragData = event.active.data.current as VideosDragData | undefined;
    const dropData = event.over?.data.current as FolderDropData | undefined;
    if (!dragData || !dropData) {
      return;
    }

    const targetFolderExternalId = dropData.folderExternalId;

    if (dragData.type === "folder") {
      const { folder } = dragData;
      if (
        folder.externalId === targetFolderExternalId ||
        folder.parentFolderExternalId === targetFolderExternalId
      ) {
        return;
      }
      moveFolderMutation.mutate({
        folderExternalId: folder.externalId,
        data: { parentFolderExternalId: targetFolderExternalId },
      });
      return;
    }

    if ((folderExternalId ?? null) === targetFolderExternalId) {
      return;
    }
    moveVideoMutation.mutate({
      videoExternalId: dragData.video.externalId,
      folderExternalId: targetFolderExternalId,
    });
  };

  const {
    data: contents,
    isPending,
    isError,
  } = useFolderContentsQuery(teamExternalId!, folderExternalId, sort);

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={pointerWithin}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      onDragCancel={markDragEnded}
    >
      <AppHeader
        actionChildren={
          !isMobile && (
            <>
              <NotificationsDropdown />
              <ReferralOverviewDialog source="VIDEOS_LIST">
                <Button
                  size="sm"
                  variant="tertiarySecondary"
                  icon={<IconGift />}
                  iconPosition="leading"
                >
                  Earn $100
                </Button>
              </ReferralOverviewDialog>
              <Button
                size="sm"
                onClick={() =>
                  handleCreateVideo(navigate, teamExternalId!, folderExternalId)
                }
              >
                New video
              </Button>
            </>
          )
        }
      >
        <VideoSearchBar />
      </AppHeader>
      <div className="px-4 md:px-6">
        <div className="mt-1 flex items-end justify-between border-b border-border py-3">
          <div className="flex min-w-0 items-center pr-8">
            {folderExternalId ? (
              contents?.folder && (
                <>
                  <Breadcrumb
                    teamExternalId={teamExternalId!}
                    contents={contents}
                  />
                  <CreateFolderButton onClick={onCreateFolder} />
                </>
              )
            ) : (
              <>
                <h2 className="text-md mr-3 font-semibold">Videos</h2>
                <CreateFolderButton onClick={onCreateFolder} />
              </>
            )}
          </div>
          <Select
            value={sort}
            onValueChange={(value) => onSortChange(value as VideoSort)}
          >
            <SelectTrigger
              variant="secondary"
              className="-mb-1.5 h-fit w-auto shrink-0 py-2"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              <SelectItem value="lastUpdated">Last updated</SelectItem>
              <SelectItem value="dateCreated">Date created</SelectItem>
              <SelectItem value="name">Alphabetically</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      {isPending ? (
        <div className="flex flex-1 items-center justify-center self-stretch">
          <LoadingSpinner size={32} />
        </div>
      ) : isError ? (
        <div className="flex flex-1 items-center justify-center gap-2">
          <IconExclamationCircle size={24} className="text-error" />
          <p className="font-medium">
            {folderExternalId
              ? "Failed to load folder"
              : "Failed to load videos"}
          </p>
        </div>
      ) : (
        <div
          className="flex-1 animate-fade-in overflow-y-auto px-4 py-8 md:px-6"
          onClickCapture={(e) => {
            if (dragJustEndedRef.current) {
              e.preventDefault();
              e.stopPropagation();
            }
          }}
        >
          <div className="flex h-full flex-col gap-10">
            <FoldersSection
              teamExternalId={teamExternalId!}
              contents={contents}
            />
            {folderExternalId ? (
              <FolderVideoList
                videos={contents.videos}
                folderExternalId={folderExternalId}
                hasFolders={contents.folders.length > 0}
                onCreateFolder={onCreateFolder}
              />
            ) : (
              <VideoList hasFolders={contents.folders.length > 0} sort={sort} />
            )}
          </div>
        </div>
      )}
      <DragOverlay
        dropAnimation={null}
        modifiers={[snapAboveCursor]}
        style={{ width: "auto", height: "auto" }}
      >
        {activeDrag && (
          <div className="flex w-fit max-w-72 items-center gap-3 rounded-xl border border-border bg-white px-4 py-3 shadow-lg">
            {activeDrag.type === "folder" ? (
              <IconFolder size={20} className="shrink-0 text-text-secondary" />
            ) : (
              <IconVideo size={20} className="shrink-0 text-text-secondary" />
            )}
            <span className="line-clamp-1 font-semibold">
              {activeDrag.type === "folder"
                ? activeDrag.folder.name
                : activeDrag.video.name || "Untitled video"}
            </span>
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}

function CreateFolderButton({ onClick }: { onClick: () => void }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          size="iconXs"
          variant="outline"
          onClick={onClick}
          className="size-7 shrink-0"
          aria-label="New folder"
        >
          <IconFolderPlus />
        </Button>
      </TooltipTrigger>
      <TooltipContent>Create a new folder</TooltipContent>
    </Tooltip>
  );
}

function FolderVideoList({
  videos,
  folderExternalId,
  hasFolders,
  onCreateFolder,
}: {
  videos: PagedVideoItemDto[];
  folderExternalId: string;
  hasFolders: boolean;
  onCreateFolder: () => void;
}) {
  const navigate = useNavigate();
  const { teamExternalId } = useParams();

  if (videos.length === 0 && !hasFolders) {
    return (
      <div className="flex flex-1 flex-col">
        <EmptyState
          icon={<IconVideoOff size={24} />}
          header={<span className="text-lg">No videos or folders here</span>}
          subHeader={<span>Create a new video or folder to get started.</span>}
          primaryActionLabel="New video"
          primaryActionOnClick={() =>
            handleCreateVideo(navigate, teamExternalId!, folderExternalId)
          }
          secondaryActionLabel="New folder"
          secondaryActionOnClick={onCreateFolder}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col gap-4">
      {hasFolders && (
        <h2 className="text-sm font-semibold text-text-secondary">Videos</h2>
      )}
      {videos.length === 0 ? (
        <p className="text-sm text-text-secondary">No videos in this folder.</p>
      ) : (
        <div className="grid grid-cols-1 gap-x-6 gap-y-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {videos.map((video) => (
            <VideoListItem
              key={video.externalId}
              video={video}
              folderExternalId={folderExternalId}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function VideoList({
  hasFolders,
  sort,
}: {
  hasFolders: boolean;
  sort: VideoSort;
}) {
  const { teamExternalId } = useParams();

  const {
    data: videos,
    isPending,
    isError,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = usePagedVideosQuery(teamExternalId!, sort);
  const { ref, inView } = useInView();

  useEffect(() => {
    if (inView && !isFetching && !isError && hasNextPage) {
      fetchNextPage();
    }
  }, [inView, isFetching, isError, hasNextPage, fetchNextPage]);

  if (isPending) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size={32} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center gap-2 py-12">
        <IconExclamationCircle size={24} className="text-error" />
        <p className="font-medium">Failed to load videos</p>
      </div>
    );
  }

  const flatVideos = videos.pages.flatMap((page) => page.videos);

  if (flatVideos.length === 0 && !hasFolders) {
    return (
      <VideosEmptyState
        teamExternalId={teamExternalId!}
        handleCreateVideo={handleCreateVideo}
      />
    );
  }

  return (
    <div className="flex flex-col gap-4">
      {hasFolders && (
        <h2 className="text-sm font-semibold text-text-secondary">Videos</h2>
      )}
      {flatVideos.length === 0 ? (
        <p className="text-sm text-text-secondary">No videos yet.</p>
      ) : (
        <div className="grid grid-cols-1 gap-x-6 gap-y-8 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {flatVideos.map((video) => (
            <VideoListItem key={video.externalId} video={video} />
          ))}
        </div>
      )}
      <div ref={ref} className="flex w-full items-center justify-center">
        {isFetchingNextPage && <LoadingSpinner size={24} />}
      </div>
    </div>
  );
}
